// Feb 18, 2026 — Creator-facing popup when a silo price change has
// real-world consequences for existing subscribers.
//
// Fires when:
//   - A `PATCH /silos/{id}` request changes `subscriber_price_cents`
//   - AND that silo has >= 1 active subscriber
//
// Backend doesn't apply the change immediately — it stamps a
// `pending_price_change` on the silo. This modal lets the creator
// explicitly confirm via POST /silos/{id}/apply-price-change, or back
// out via /cancel-price-change.
//
// State machine (mirrors the backend):
//   paid_to_paid  → demote all subs to "Fan of." Stripe subs cancelled.
//                   Subscribers must re-subscribe at the new price.
//   paid_to_free  → keep subscribers active, flip to free-tier.
//                   Stripe subs cancelled so monthly charges stop.
//   free_to_paid  → demote all subs to "Fan of." Subscribe pill reappears.
import { useState } from "react";
import { Warning, Heart, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt$ = (c) => {
  const d = (c || 0) / 100;
  return `$${d.toFixed(d % 1 === 0 ? 0 : 2)}`;
};

export default function PricingChangeModal({ open, siloId, siloName, pending, onApplied, onCancelled, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!open || !pending) return null;

  const { direction, old_price_cents, new_price_cents, affected_count } = pending;

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/silos/${siloId}/apply-price-change`);
      if (direction === "paid_to_free") {
        toast.success(`${r.data.transitioned_to_free_count} subscriber(s) kept their access — now free.`);
      } else {
        toast.success(`${r.data.demoted_count} subscriber(s) moved to Fan-of. Subscribe pill is back for them.`);
      }
      if (onApplied) onApplied(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply price change.");
      setBusy(false);
      return;
    }
    setBusy(false);
    onClose();
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/silos/${siloId}/cancel-price-change`);
      toast("Price change cancelled. Your previous price is back in place.");
      if (onCancelled) onCancelled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel.");
    } finally {
      setBusy(false);
      onClose();
    }
  };

  // Direction-specific copy
  let title, summary, consequence, primaryLabel;
  if (direction === "paid_to_free") {
    title = "Going free — let's confirm.";
    summary = `You're dropping the price from ${fmt$(old_price_cents)}/mo to free. Your ${affected_count} current subscriber${affected_count === 1 ? "" : "s"} will keep full access — they just won't be charged anymore.`;
    consequence = "Existing Stripe subscriptions will be cancelled so monthly charges stop. Subscribers stay marked as subscribed (no demotion). If you go back to paid later, they'll be demoted to Fan-of and asked to re-subscribe.";
    primaryLabel = `Drop price to free · keep ${affected_count} subscriber${affected_count === 1 ? "" : "s"}`;
  } else if (direction === "free_to_paid") {
    title = `Charging for access — ${affected_count} fan${affected_count === 1 ? " is" : "s are"} affected.`;
    summary = `You're moving from free to ${fmt$(new_price_cents)}/mo. Your ${affected_count} current free-tier subscriber${affected_count === 1 ? "" : "s"} will be demoted back to "Fan of" status.`;
    consequence = "They'll see the Subscribe pill reappear on your Public Q&A. Their bookmarks revert to ghost mode (top one kept, rest hidden). They're back on standard fan limits until they re-subscribe at the new price. No email — the reappearing pill is the signal.";
    primaryLabel = `Apply ${fmt$(new_price_cents)}/mo · demote ${affected_count}`;
  } else {
    title = `Changing your price — ${affected_count} subscriber${affected_count === 1 ? "" : "s"} will be affected.`;
    summary = `You're changing from ${fmt$(old_price_cents)}/mo to ${fmt$(new_price_cents)}/mo. All ${affected_count} current subscriber${affected_count === 1 ? "" : "s"} will be demoted to "Fan of" so they consciously opt in to the new price.`;
    consequence = "Existing Stripe subscriptions will be cancelled. Subscribers see the Subscribe pill reappear on your Public Q&A — that's the signal. Their bookmarks revert to ghost mode. No email is sent; they re-engage when they see the change.";
    primaryLabel = `Apply ${fmt$(new_price_cents)}/mo · demote ${affected_count}`;
  }

  const isDrop = (new_price_cents || 0) < (old_price_cents || 0);
  const DirIcon = direction === "paid_to_free" ? Heart : (isDrop ? ArrowDown : ArrowUp);
  const accent = direction === "paid_to_free" ? "#4A5D4E" : "#C16E5A";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="pricing-change-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="modal-safe bg-[#FDFCF8] w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl border hairline overflow-hidden"
        data-testid="pricing-change-modal"
      >
        <div className="px-6 py-5 border-b hairline flex items-start gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${accent}22`, color: accent }}>
            <DirIcon size={18} weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">{siloName}</div>
            <h2 className="font-serif-display text-xl text-[#1C1B1A] mt-0.5 leading-tight">{title}</h2>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#1C1B1A] leading-relaxed">{summary}</p>
          <div className="text-xs text-[#6B6A68] leading-relaxed bg-[#F0F1EE] rounded-md p-4">
            <Warning size={12} weight="duotone" className="inline mr-1 text-[#C16E5A]" />
            {consequence}
          </div>
        </div>

        <div className="px-6 py-4 border-t hairline flex flex-col sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            data-testid="pricing-change-cancel"
            className="px-4 py-2.5 rounded-md border hairline text-sm text-[#1C1B1A] hover:border-[#1C1B1A] disabled:opacity-50"
          >
            Cancel · keep {fmt$(old_price_cents)}/mo
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={busy}
            data-testid="pricing-change-apply"
            className="px-4 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60"
          >
            {busy ? "Applying…" : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
